<template>
  <div
    ref="root"
    :class="$style.addressSearch"
  >
    <BaseInput
      v-model="localValue"
      :required="required"
      :label="$t('global.address')"
      @focus="onFocus"
      v-test="`${testId}-address`"
    />
    <div
      v-show="isSuggestionsExpanded"
      :class="$style.suggestionsList"
      v-test="`${testId}-address-search-list`"
    >
      <div
        v-if="suggestionsLoading || isSearchPending"
        :class="$style.spinner"
      >
        <BaseSpinner inline />
      </div>
      <template v-else>
        <div
          v-for="suggestion in suggestions"
          :key="suggestion.id"
          :class="$style.suggestionItem"
          tabindex="0"
          @click="selectSuggestion(suggestion.id)"
          @keyup.enter="selectSuggestion(suggestion.id)"
          v-test="`${testId}-address-search-result`"
        >
          <div :class="$style.suggestionItemInner">
            {{ suggestion.label }}
          </div>
        </div>
        <div
          v-if="!suggestions.length"
          :class="$style.noResults"
        >
          {{ $t('global.no_results') }}
        </div>
      </template>
    </div>
  </div>
</template>

<script
  lang="ts"
  setup
>
import { useLazyQuery } from '@vue/apollo-composable';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { ADDRESS_SUGGESTIONS } from './graphql';

const props = withDefaults(
  defineProps<{
    modelValue?: string;
    required?: boolean;
    testId?: string;
  }>(),
  {
    modelValue: '',
    required: false,
    testId: 'customer',
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'update:notFound', value: boolean): void;
  (e: 'select', id: string): void;
}>();

const suggestions = ref<{ id: string; label: string }[]>([]);
const isSuggestionsExpanded = ref(false);
// True while a search is scheduled or in flight, so "no results" never flashes early.
const isSearchPending = ref(false);
const root = ref<HTMLElement | null>(null);
let searchDebounceTimeout: ReturnType<typeof setTimeout>;

const {
  load: loadSuggestions,
  refetch: refetchSuggestions,
  loading: suggestionsLoading,
  onResult: onSuggestionsResult,
  onError: onSuggestionsError,
} = useLazyQuery(ADDRESS_SUGGESTIONS);

onSuggestionsResult(({ data }) => {
  const results = data?.addressSuggestions ?? [];

  emit('update:notFound', results.length === 0);

  suggestions.value = results;
  isSearchPending.value = false;
});

onSuggestionsError(() => {
  suggestions.value = [];
  isSearchPending.value = false;
});

const scheduleSearch = (query: string) => {
  clearTimeout(searchDebounceTimeout);

  if (!query) {
    suggestions.value = [];
    isSuggestionsExpanded.value = false;
    isSearchPending.value = false;
    return;
  }

  isSuggestionsExpanded.value = true;
  isSearchPending.value = true;

  searchDebounceTimeout = setTimeout(() => {
    const variables = { query };

    // Prevents an unhandled rejection; errors are handled in onSuggestionsError.
    (
      loadSuggestions(undefined, variables) || refetchSuggestions(variables)
    )?.catch(() => {});
  }, 300);
};

const localValue = computed({
  get: () => props.modelValue,
  set: (value: string) => {
    emit('update:modelValue', value);
    scheduleSearch(value);
  },
});

const onFocus = () => {
  if (!props.modelValue) {
    return;
  }

  isSuggestionsExpanded.value = true;

  if (!suggestions.value.length) {
    scheduleSearch(props.modelValue);
  }
};

const onDocumentClick = (event: MouseEvent) => {
  if (
    isSuggestionsExpanded.value &&
    root.value &&
    !root.value.contains(event.target as Node)
  ) {
    isSuggestionsExpanded.value = false;
  }
};

onMounted(() => document.addEventListener('click', onDocumentClick));
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick);
  clearTimeout(searchDebounceTimeout);
});

const selectSuggestion = (id: string) => {
  isSuggestionsExpanded.value = false;
  emit('select', id);
};
</script>

<style
  lang="scss"
  module
>
.addressSearch {
  position: relative;
}

.suggestionsList {
  @include floating-list;
  position: absolute;
  top: calc(100% + #{$spacing * 0.25});
  left: 0;
  right: 0;
  max-height: 240px;
  overflow-y: auto;
}

.suggestionItem {
  @include floating-list-item;
}

.suggestionItemInner,
.spinner,
.noResults {
  @include floating-list-item-inner;
}
</style>
